import { getWindowData } from 'services/ScormData/ScormData'
import { parsePako, stringifyPako } from 'utils/gzip'

import type { CmiElementConfig, ScormRuntimeVersion } from './common'
import { CMI_CONFIG } from './common'
import { CrossDomainClient } from './postmessage-client'
import type { CMIElement, IInteractionData, ScormSuspendData } from './types'

let client: CrossDomainClient | null = null
let api: ReturnType<CrossDomainClient['createAPI']> | null = null

type CmiData = { data: string }

const dataStorage = new Map<string, CmiData>()
const pendingDispatch = new Map<CMIElement, string>()
let dispatchFlushTimer: ReturnType<typeof setTimeout> | null = null
let dispatchFlushPromise: Promise<boolean> | null = null
let forceCommitPromise: Promise<void> | null = null // Queue for forced commits
const DISPATCH_FLUSH_DELAY = 1500

const clearDispatchTimer = (): void => {
  if (dispatchFlushTimer !== null) {
    clearTimeout(dispatchFlushTimer)
    dispatchFlushTimer = null
  }
}

const scheduleDispatchFlush = (): void => {
  if (dispatchFlushTimer !== null) {
    return
  }

  dispatchFlushTimer = setTimeout(() => {
    dispatchFlushTimer = null
    void flushPendingDispatch()
  }, DISPATCH_FLUSH_DELAY)
}

const flushPendingDispatch = async (): Promise<boolean> => {
  if (!api) {
    pendingDispatch.clear()
    clearDispatchTimer()
    dispatchFlushPromise = null
    return false
  }

  if (dispatchFlushPromise) {
    return dispatchFlushPromise
  }

  clearDispatchTimer()

  if (pendingDispatch.size === 0) {
    return true
  }

  const values = Array.from(pendingDispatch.entries()).map(([element, value]) => ({
    element,
    value,
  }))
  pendingDispatch.clear()

  dispatchFlushPromise = api
    .setBatch(values)
    .then((result) => {
      dispatchFlushPromise = null
      return result
    })
    .catch((error) => {
      dispatchFlushPromise = null
      console.error('[SCORM_API_Wrapper] Failed to flush dispatch batch:', error)
      values.forEach(({ element, value }) => pendingDispatch.set(element, value))
      scheduleDispatchFlush()
      return false
    })

  return dispatchFlushPromise
}

const queueDispatchValue = (cmiKey: CMIElement, data: string, force: boolean): void => {
  if (!api) {
    return
  }

  pendingDispatch.set(cmiKey, data)

  if (force) {
    // Queue forced commits to prevent race conditions
    const executeForceCommit = async () => {
      // Wait for any existing force commit to complete
      if (forceCommitPromise) {
        await forceCommitPromise
      }

      // Create new promise for this force commit
      forceCommitPromise = (async () => {
        const flushed = await flushPendingDispatch()
        if (flushed) {
          await api!.commit()
        }
      })()

      await forceCommitPromise
      forceCommitPromise = null
    }

    void executeForceCommit()
  } else {
    scheduleDispatchFlush()
  }
}

const resetDispatchQueue = (): void => {
  clearDispatchTimer()
  pendingDispatch.clear()
  dispatchFlushPromise = null
}

const flushAndCommit = async (): Promise<boolean> => {
  if (!api) {
    return false
  }

  const flushed = await flushPendingDispatch()
  if (!flushed) {
    return false
  }

  const committed = await api.commit()
  return committed
}

const ensureExitValue = async (value: string): Promise<boolean> => {
  if (!api) {
    return false
  }

  const result = await api.setvalue('cmi.exit', value)
  return result
}

const isInteractionElement = (key: CMIElement): boolean => key.startsWith('cmi.interactions.')

const getCmiConfig = (key: CMIElement): CmiElementConfig => {
  const config = CMI_CONFIG[key]
  if (config) {
    return config
  }

  if (isInteractionElement(key)) {
    return { saveType: 'suspend', default: '' }
  }

  return { saveType: 'force', default: '' }
}

const setValue = (
  cmiKey: CMIElement,
  data: string,
  force = getCmiConfig(cmiKey).saveType === 'force',
) => {
  const currentData = dataStorage.get(cmiKey)
  if (currentData?.data === data) {
    return false
  }

  dataStorage.set(cmiKey, { data })

  queueDispatchValue(cmiKey, data, force)

  return true
}

const readOnInit = async () => {
  if (!api) {
    return
  }

  const currentVersion = api.getVersion()

  return Promise.all(
    Object.keys(CMI_CONFIG).map(async (key) => {
      const config = getCmiConfig(key as CMIElement)
      if (config.initRead) {
        if (config.scormVersion && config.scormVersion !== currentVersion) {
          return
        }
        const value = await api!.loadvalue(key as CMIElement)
        if (value !== 'false') {
          dataStorage.set(key, { data: value })
        }
      }
    }),
  )
}

const getValue = (key: CMIElement): string => {
  if (!api) {
    return ''
  }

  const data = dataStorage.get(key)
  if (data) {
    return data.data
  }

  // Note: Cannot fetch from LMS synchronously with CrossDomainAPI
  // Values must be pre-loaded in initRead or use getValueAsync
  console.warn(
    `SCORM_API_Wrapper: getValue: ${key} not found in dataStorage. Consider adding it to CMI_CONFIG.initRead. Using default value.`,
  )

  // Use default value and cache it
  const defaultValue = getCmiConfig(key).default ?? ''
  dataStorage.set(key, { data: defaultValue })

  return defaultValue
}

// Async version that can fetch from LMS if needed
const getValueAsync = async (key: CMIElement): Promise<string> => {
  if (!api) {
    return ''
  }

  const data = dataStorage.get(key)
  if (data) {
    return data.data
  }

  // Try to fetch from LMS
  console.info(`SCORM_API_Wrapper: getValueAsync: Fetching ${key} from LMS`)
  const lmsValue = await api.loadvalue(key)

  if (lmsValue !== null && lmsValue !== undefined && lmsValue !== '') {
    // Cache the fetched value
    dataStorage.set(key, { data: lmsValue })
    return lmsValue
  }

  // Use default value and cache it
  const defaultValue = getCmiConfig(key).default ?? ''
  dataStorage.set(key, { data: defaultValue })

  return defaultValue
}

const init = async (debugMode = false): Promise<void> => {
  // Check for scorm_proxy parameter
  const urlParams = new URLSearchParams(window.location.search)
  const hasScormProxy = urlParams.get('scorm_proxy') === 'postmessage'

  if (!hasScormProxy) {
    console.warn(
      '[SCORM_API_Wrapper] Missing scorm_proxy parameter. Content opened directly, not via SCORM driver.',
    )
    return
  }

  // Initialize CrossDomainClient
  client = new CrossDomainClient({ debug: debugMode })
  api = client.createAPI()

  // v1.1.0: Get save settings before initialize
  const windowData = getWindowData()
  const runtimeSettings = windowData?.settings?.runtimeSettings

  // Initialize connection and pass save settings
  const initialized = await api.initialize(
    runtimeSettings
      ? {
          suspendDataInterval: runtimeSettings.suspendDataInterval,
          suspendDataType: runtimeSettings.suspendDataType,
        }
      : undefined,
  )

  if (!initialized) {
    console.error('[SCORM_API_Wrapper] Failed to initialize SCORM API')
    return
  }

  await readOnInit()

  // eslint-disable-next-line no-console
  console.log(Object.fromEntries(dataStorage.entries()))

  const completionStatus = getValue('cmi.completion_status')
  const successStatus = getValue('cmi.success_status')

  if (api) {
    void api.setvalue('cmi.completion_status', completionStatus || 'incomplete')
    void api.setvalue('cmi.success_status', successStatus || 'unknown')
  }
}

const isConnected = (): boolean => api?.isConnected() ?? false

const getVersion = (): ScormRuntimeVersion => api?.getVersion() ?? 'none'

export const DEFAULT_SUSPEND_DATA = {
  blocks: {},
  sections: {},
  course: {},
  elements: {},
}

const setSuspendData = (data: ScormSuspendData) => {
  setValue('cmi.suspend_data', stringifyPako(data))
  // flushSuspendThrottled()
}

const getSuspendData = (): ScormSuspendData => {
  try {
    const data = getValue('cmi.suspend_data')
    // TODO scorm can save 4000 chars in v1.2 and 64000 chars in v2004
    // if more - JSON.parse will throw an error
    if (data && data !== 'false') {
      console.info('getSuspendData data scorm', data)
      const parsedData = parsePako<typeof DEFAULT_SUSPEND_DATA>(data)
      return {
        blocks: parsedData.blocks || DEFAULT_SUSPEND_DATA.blocks,
        sections: parsedData.sections || DEFAULT_SUSPEND_DATA.sections,
        course: parsedData.course || DEFAULT_SUSPEND_DATA.course,
        elements: parsedData.elements || DEFAULT_SUSPEND_DATA.elements,
      }
    }
  } catch (err) {
    console.info(err, 'getSuspendData error')
    return DEFAULT_SUSPEND_DATA
  }

  return DEFAULT_SUSPEND_DATA
}

const setValueInteraction = (index: number, data: IInteractionData) => {
  const prefix = `cmi.interactions.${index}`

  // Set all required values
  setValue(`${prefix}.id` as CMIElement, data.id)
  setValue(`${prefix}.type` as CMIElement, data.type)

  if (data.student_response) {
    setValue(`${prefix}.student_response` as CMIElement, data.student_response)
  }
  if (data.learner_response) {
    setValue(`${prefix}.learner_response` as CMIElement, data.learner_response)
  }

  setValue(`${prefix}.correct_responses.0.pattern` as CMIElement, data.correctAnswer)
  setValue(`${prefix}.weighting` as CMIElement, '1')
  setValue(`${prefix}.result` as CMIElement, data.result)
  setValue(`${prefix}.latency` as CMIElement, data.latency)

  if (data.timestamp) {
    setValue(`${prefix}.timestamp` as CMIElement, data.timestamp)
  }
  if (data.time) {
    setValue(`${prefix}.time` as CMIElement, data.time)
  }
}

const commit = (): void => {
  if (!api) {
    return
  }

  void flushAndCommit()
}

/**
 * Unified terminate method - replaces exit/suspend/resetAsync
 * @param options.mode - 'exit' (default): finalize course and optionally close window
 *                       'suspend': save progress for later resumption
 *                       'reset': clear all data and restart
 *                       'logout': complete course and end attempt
 * @param options.closeWindow - request parent STUB to close window (only for 'exit' mode)
 * @param options.resetLocation - new location after reset (only for 'reset' mode)
 */
const terminate = async (options?: {
  mode?: 'exit' | 'suspend' | 'reset' | 'logout'
  closeWindow?: boolean
  resetLocation?: string
}): Promise<boolean> => {
  if (!api) {
    return false
  }

  const mode = options?.mode || 'exit'

  const performTerminate = async (): Promise<boolean> => {
    if (!api) {
      return false
    }
    const terminateResult = await api.terminate()
    if (!terminateResult) {
      console.error('[SCORM_API_Wrapper] Terminate failed')
      return false
    }
    return true
  }

  if (mode === 'reset') {
    resetDispatchQueue()
    for (const cmiConfigKey in CMI_CONFIG) {
      const config = CMI_CONFIG[cmiConfigKey as CMIElement]
      if (config) {
        dataStorage.set(cmiConfigKey as CMIElement, {
          data: config.default,
        })
      }
    }

    const resetValues = [
      { element: 'cmi.completion_status', value: 'incomplete' },
      { element: 'cmi.success_status', value: 'unknown' },
      { element: 'cmi.score.raw', value: '0' },
      { element: 'cmi.score.scaled', value: '0' },
      { element: 'cmi.progress_measure', value: '0' },
      { element: 'cmi.suspend_data', value: '' },
    ]

    if (options?.resetLocation !== undefined) {
      resetValues.push({ element: 'cmi.location', value: options.resetLocation })
    }

    const batchResult = await api.setBatch(resetValues)
    if (!batchResult) {
      console.error('[SCORM_API_Wrapper] Failed to send reset batch')
      return false
    }

    dataStorage.set('cmi.completion_status', { data: 'incomplete' })
    dataStorage.set('cmi.success_status', { data: 'unknown' })
    dataStorage.set('cmi.score.raw', { data: '0' })
    dataStorage.set('cmi.score.scaled', { data: '0' })
    dataStorage.set('cmi.progress_measure', { data: '0' })
    dataStorage.set('cmi.suspend_data', { data: '' })
    if (options?.resetLocation !== undefined) {
      dataStorage.set('cmi.location', { data: options.resetLocation })
    }

    const committed = await flushAndCommit()
    if (!committed) {
      console.error('[SCORM_API_Wrapper] Commit failed after reset batch')
      return false
    }

    // Don't use 'logout' as it may cause LMS to close window
    // Use empty string for normal exit that allows continuation
    const exitResult = await ensureExitValue('')
    if (!exitResult) {
      console.warn('[SCORM_API_Wrapper] Failed to set cmi.exit for reset')
    }

    const finalCommit = await flushAndCommit()
    if (!finalCommit) {
      console.error('[SCORM_API_Wrapper] Commit failed before reset terminate')
      return false
    }

    // Don't terminate on reset - just clear data and let page reload handle re-init
    // Terminating here may cause LMS to close window
    return true
  }

  const pendingCommitted = await flushAndCommit()
  if (!pendingCommitted) {
    console.error('[SCORM_API_Wrapper] Failed to commit pending values before terminate')
    return false
  }

  // Set appropriate exit value based on mode
  let exitValue = ''
  if (mode === 'suspend') {
    exitValue = 'suspend'
  } else if (mode === 'logout') {
    exitValue = 'logout'
  }

  const exitSet = await ensureExitValue(exitValue)
  if (!exitSet) {
    console.warn('[SCORM_API_Wrapper] Failed to set cmi.exit before termination')
  }

  const committed = await flushAndCommit()
  if (!committed) {
    console.error('[SCORM_API_Wrapper] Commit failed before terminate')
    return false
  }

  const terminateResult = await performTerminate()
  if (!terminateResult) {
    return false
  }

  if (mode === 'exit' && (options?.closeWindow ?? false)) {
    await new Promise((resolve) => setTimeout(resolve, 100))
    await api.closeWindow()
  }

  return true
}

const debug = (action?: 'save' | 'getArray' | 'getInfo'): string[] | object | void => {
  if (action === 'save') {
    // Force immediate save of all pending data
    console.info('[SCORM_API_Wrapper] Debug: forcing save of all pending data')
    void flushAndCommit()
    return
  }

  if (action === 'getArray') {
    console.warn('[SCORM_API_Wrapper] Debug array not available')
    return []
  }

  return getDebugInfo()
}

const getDebugInfo = () => {
  return {
    version: getVersion(),
    isConnected: isConnected(),
    dataStorageSize: dataStorage.size,
  }
}

export interface IScormLearnerInfo {
  learnerId: string
  learnerName: string
}

const getLearnerInfo = (): IScormLearnerInfo => {
  const version = getVersion()

  // SCORM 2004 uses cmi.learner_id/cmi.learner_name
  // SCORM 1.2 uses cmi.core.student_id/cmi.core.student_name
  const learnerId = version === '1.2' ? getValue('cmi.core.student_id') : getValue('cmi.learner_id')
  const learnerName =
    version === '1.2' ? getValue('cmi.core.student_name') : getValue('cmi.learner_name')

  return {
    learnerId: learnerId || '',
    learnerName: learnerName || '',
  }
}

const SCORM_API_Wrapper = {
  init,
  isConnected,
  getVersion,

  setValue,
  getValue,
  setSuspendData,
  getSuspendData,
  setInteraction: setValueInteraction,

  commit,
  terminate,

  debug,

  getValueAsync,
  getLearnerInfo,
}

export { SCORM_API_Wrapper }
